
import { Box, FormHelperText, Grid, makeStyles, Typography } from "@material-ui/core";
import MapOutlinedIcon from '@material-ui/icons/MapOutlined';
import {
  CurrencyField,
  MultiSelectField,
  NumberFormat,
  RadioField,
  SelectField,
  Slider,
  TextField,
  yesNoOptions,
} from '@roc/ui';
import { isNotBlank, propertyStates } from "@roc/feature-utils";
import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { RentalPortfolioStore } from "../../stores/rentalPortfolioStore";
import { RoundedBackgroundIcon } from "../common/roundedBackgroundIcon";
import { OneToolCardBorder } from "../fixFlip/oneToolCard";
import { FORM_MAX_WIDTH } from "../../utils/constants";
import { TitleDivider } from "../common/titleDivider";
import { PropertyTypesForm } from "./propertyTypesForm";
import { PortfolioDetailsForm } from "./portfolioDetailsForm";

const areSelectedCountiesValid = (counties, selectedCounties) => {
  if (!selectedCounties) {
    return false;
  }
  const countyValues = new Set(counties.map(c => c.value));
  return selectedCounties.every(sc => countyValues.has(sc.value));
};

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    position: 'relative',
    maxWidth: 1300,
    margin: 'auto',
  },
  root: {
    display: 'flex',
    backgroundColor: '#FFF4E5',
    borderRadius: '8px',
    marginTop: '16px',
    padding: '14px 16px',
    alignItems: 'center',
  },
  icon: {
    color: '#EF6C00',
    marginRight: '12px',
  },
  title: {
    fontWeight: 'bold',
    color: '#663C00',
  },
}));

interface Props {
  store: RentalPortfolioStore;
}

export const PortfolioInformationForm = observer(({ store }: Props) => {

  const { portfolioInformationFormStore } = store;
  const [counties, setCounties] = useState([]);

  const { form } = portfolioInformationFormStore;

  const error = form.fields.propertyCounties.error;

  const classes = useStyles();

  useEffect(() => {
    portfolioInformationFormStore.fetchStateCounties();
  }, [portfolioInformationFormStore]);

  useEffect(() => {
    if (form.fields.propertyStates.value) {
      setCounties(portfolioInformationFormStore.getCounties());

    } else {
      setCounties([]);
    }
  }, [
    form.fields.propertyStates.value,
    form.fields.propertyCounties.value,
    portfolioInformationFormStore.allCounties
  ]);

  const handleChangeState = (value) => {
    portfolioInformationFormStore.onFieldChange("propertyStates", value);
    cleanupPropertyCountiesIfInvalid();

  }

  const cleanupPropertyCountiesIfInvalid = () => {
    const counties = portfolioInformationFormStore.getCounties()
    const obj: any = portfolioInformationFormStore.getFlattenedValues();
    if (!areSelectedCountiesValid(counties, obj.propertyCounties)) {
      portfolioInformationFormStore.onFieldChange('propertyCounties', null);
    }
  }

  const propertyStatesById = propertyStates.map(state => ({
    value: state.id,
    label: state.label
  }));

  return (
    <Box display="flex" flexDirection="column" gridGap="20px">
      <OneToolCardBorder>
        <Grid
          container
          spacing={2}
          className={classes.container}
          style={{ maxWidth: FORM_MAX_WIDTH, marginBottom: '0.5rem' }}
        >
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <RoundedBackgroundIcon
                  Icon={MapOutlinedIcon}
                  iconColor="#6D6D6D"
                />
              </Grid>
              <Grid item>
                <Typography variant="h4" style={{ color: '#6D6D6D' }}>
                  Portfolio Properties
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectField
              label={'Select Property State'}
              name={'propertyStates'}
              value={form.fields.propertyStates.value}
              error={isNotBlank(form.fields.propertyStates.error)}
              errorText={form.fields.propertyStates.error}
              options={propertyStatesById}
              onChange={handleChangeState}
              variant="outlined"
              fullWidth
              shrink
              required
              testId="propertyStates"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MultiSelectField
              label={'Select Property State'}
              name={'propertyCounties'}
              options={counties}
              getOptionSelected={(option, selectedValue) => {
                return option.value == selectedValue.value;
              }}
              value={
                form.fields.propertyCounties.value
                  ? form.fields.propertyCounties.value
                  : []
              }
              onChange={(e, value) => {
                portfolioInformationFormStore.onFieldChange('propertyCounties', value);
              }}
              renderInput={params => (
                <TextField
                  helperText={error}
                  error={isNotBlank(error)}
                  testId="propertyCounties"
                  {...params}
                  variant="outlined"
                />
              )}
              fullWidth
              required
              testId="propertyCounties"
            />
          </Grid>
          <Grid
            item
            xs={12}
            style={{ marginTop: '1rem', marginBottom: '1rem' }}
          >
            <TitleDivider title="Property Types" />
            <PropertyTypesForm portfolioInformationFormStore={portfolioInformationFormStore} />
          </Grid>
        </Grid>
      </OneToolCardBorder>
      <OneToolCardBorder>
        <PortfolioDetailsForm portfolioInformationFormStore={portfolioInformationFormStore} />
      </OneToolCardBorder>
    </Box>
  );

});
